import { useState } from 'react';
import type { ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';
import { Button } from './primitives';
import { FinishSignup } from './FinishSignup';
import './AuthGate.css';

/**
 * Path prefixes that must render WITHOUT a session — the router (App.tsx)
 * registers these outside the AppShell and they manage their own (no-)auth:
 *   - /coach-review/<token>  the pro-coach redemption page; it signs the
 *                            coach in ITSELF (calls coach-review-redeem +
 *                            verifyOtp), so it has to run while the visitor
 *                            is still unauthenticated. Gating it here is what
 *                            previously dropped Ben on the login wall.
 *   - /legal/...             public privacy notices.
 *   - /parent/consent/...    the parental-consent flow (parent has no account
 *                            yet during these steps).
 */
const PUBLIC_PATH_PREFIXES = ['/coach-review/', '/legal/', '/parent/consent/'];

export function AuthGate({ children }: { children: ReactNode }) {
  const { session, loading, bypass, profileStatus, refreshProfile } = useAuth();
  const { pathname } = useLocation();
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  const [sending, setSending] = useState(false);

  // Public routes bypass the gate entirely — render the router straight away
  // (don't even wait on the auth probe). Checked first so an unauthenticated
  // deep link reaches its component instead of the magic-link form.
  if (PUBLIC_PATH_PREFIXES.some((p) => pathname.startsWith(p))) return <>{children}</>;

  if (loading) return null;

  // Bypass mode (dev): treat as authed + complete; render app.
  if (bypass) return <>{children}</>;

  // V1 launch posture: every real sign-in resolves to either
  // 'complete' (users row with DoB) → app, or 'missing' →
  // finish-signup form. While the public.users probe is still
  // resolving we render nothing rather than flash the app.
  if (session) {
    if (profileStatus === 'loading') return null;
    if (profileStatus === 'missing') {
      return <FinishSignup onComplete={refreshProfile} />;
    }
    return <>{children}</>;
  }

  const handleSend = async () => {
    if (!email.includes('@')) return;
    setSending(true);
    await supabase.auth.signInWithOtp({ email });
    setSent(true);
    setSending(false);
  };

  return (
    <div className="auth-gate">
      <div className="auth-gate__panel">
        <h1 className="auth-gate__brand">4CM</h1>
        {!sent ? (
          <>
            <p className="auth-gate__copy">Sign in to continue.</p>
            <input
              type="email"
              className="auth-gate__input"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') void handleSend(); }}
              placeholder="coach@example.com"
              autoFocus
            />
            <Button variant="primary" size="touchline" onClick={() => void handleSend()}>
              {sending ? 'Sending…' : 'Send magic link'}
            </Button>
          </>
        ) : (
          <p className="auth-gate__sent">
            Magic link sent to <strong>{email}</strong>. Click it on this device to sign in.
          </p>
        )}
      </div>
    </div>
  );
}
